import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { StyledTextArea } from "./styled/styledInputs";
import {
  useGenerateAiCommentsMutation,
  useConfirmAiCommentsMutation,
} from "../pages/quick-entry/recordApiSlice";
import {
  useLazyGetAiCreditsBalanceQuery,
  usePurchaseAiCreditsMutation,
  useVerifyAiCreditsMutation,
} from "../../app/subscriptionApiSlice";
import toast from "../../helpers/toast";
import PaystackPop from "@paystack/inline-js";
import { useFlutterwave, closePaymentModal } from "flutterwave-react-v3";

const EMPTY_FW_CONFIG = {
  public_key: "",
  tx_ref: "placeholder",
  amount: 0,
  currency: "NGN",
  customer: { email: "" },
  customizations: { title: "SchoolApp" },
};

function openCheckoutPopup(url) {
  const width = 520;
  const height = 760;
  const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
  const features = `width=${Math.round(width)},height=${Math.round(height)},left=${Math.round(left)},top=${Math.round(top)},popup=yes,resizable=yes,scrollbars=yes`;
  const popup = window.open(url, "paystack_checkout", features);
  if (popup) popup.focus();
  return popup;
}

/**
 * AICommentModal — generates AI report comments and lets teachers review/edit before saving.
 *
 * Props:
 *   open              boolean
 *   onClose           () => void
 *   roleType          'subject_teacher' | 'classroom_manager' | 'section_head' | 'department_head'
 *   subjectId         number | null
 *   classroomId       number | null
 *   sectionId         number | null
 *   departmentId      number | null
 *   commentType       'term' | 'midterm'
 *   commentTitle      string  e.g. "Classroom Comment"
 *   termId            number | null
 *   students          [{id, first_name, last_name}]
 *   existingComments  [{student_id, content}]   — to flag overwrites
 *   onConfirmed       () => void  — called after successful save
 */
const TONES = [
  { value: "formal", label: "Formal" },
  { value: "encouraging", label: "Encouraging" },
  { value: "strict", label: "Strict" },
  { value: "balanced", label: "Balanced" },
];

const COMMENT_LENGTHS = [
  { value: "short", label: "Short", helper: "Up to 10 words" },
  { value: "medium", label: "Medium", helper: "Up to 15 words" },
  { value: "long", label: "Long", helper: "Up to 20 words" },
];

const STEP_CONFIG = { SETUP: 0, LOADING: 1, PREVIEW: 2 };

const getStudentDisplayName = (student, fallbackId) => {
  if (!student) return `Student #${fallbackId}`;
  if (student.full_name) return student.full_name;
  if (student.name) return student.name;
  const composedName = [student.first_name, student.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (composedName) return composedName;
  return `Student #${fallbackId}`;
};

export default function AICommentModal({
  open,
  onClose,
  roleType = "classroom_manager",
  subjectId = null,
  classroomId = null,
  sectionId = null,
  departmentId = null,
  commentType = "term",
  commentTitle = "Comment",
  termId = null,
  students = [],
  existingComments = [],
  onConfirmed,
}) {
  const [step, setStep] = useState(STEP_CONFIG.SETUP);
  const [tone, setTone] = useState("formal");
  const [commentLength, setCommentLength] = useState("medium");
  const [file, setFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]); // [{student_id, name, content, selected, hasExisting}]
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Teacher AI credits purchase (shown when school quota is exhausted)
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [noEmail, setNoEmail] = useState(false);
  const [aiPurchaseAmount, setAiPurchaseAmount] = useState(50);
  const [aiPurchaseGateway, setAiPurchaseGateway] = useState("paystack");
  const [aiPurchasing, setAiPurchasing] = useState(false);
  const [aiPendingRef, setAiPendingRef] = useState(null);
  const [aiBalance, setAiBalance] = useState(0);

  // Flutterwave inline payment state
  const [fwPendingConfig, setFwPendingConfig] = useState(null);
  const fwPendingRef = useRef(null);
  const handleFlutterPayment = useFlutterwave(fwPendingConfig || EMPTY_FW_CONFIG);

  useEffect(() => {
    if (fwPendingConfig && fwPendingRef.current) {
      const onSuccess = fwPendingRef.current;
      handleFlutterPayment({
        callback: (response) => {
          closePaymentModal();
          fwPendingRef.current = null;
          setFwPendingConfig(null);
          onSuccess(response.tx_ref);
        },
        onClose: () => {
          fwPendingRef.current = null;
          setFwPendingConfig(null);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fwPendingConfig]);

  const openInlinePayment = useCallback((result, onVerifySuccess) => {
    // Deep-clone to avoid Immer-frozen RTK Query response objects
    const config = JSON.parse(JSON.stringify(result.config));
    const gatewayKey = result.gateway === "paystack" ? config.key : config.public_key;
    if (!gatewayKey) {
      const other = result.gateway === "paystack" ? "Flutterwave" : "Paystack";
      toast.error(`${result.gateway === "paystack" ? "Paystack" : "Flutterwave"} is not available. Please select ${other} instead.`);
      return;
    }
    if (result.gateway === "paystack") {
      const popup = new PaystackPop();
      popup.newTransaction({
        ...config,
        onSuccess: (tx) => onVerifySuccess(tx.reference),
        onCancel: () => toast.info("Payment cancelled"),
      });
    } else {
      fwPendingRef.current = onVerifySuccess;
      setFwPendingConfig(config);
    }
  }, []);

  const [generateAiComments] = useGenerateAiCommentsMutation();
  const [confirmAiComments] = useConfirmAiCommentsMutation();
  const [fetchAiCreditsBalance, { isFetching: aiBalanceLoading }] = useLazyGetAiCreditsBalanceQuery();
  const [purchaseAiCredits] = usePurchaseAiCreditsMutation();
  const [verifyAiCredits] = useVerifyAiCreditsMutation();

  useEffect(() => {
    const loadBalance = async () => {
      try {
        const data = await fetchAiCreditsBalance().unwrap();
        setAiBalance(data?.balance || 0);
      } catch (_) {
        // Keep modal usable even if balance fetch fails.
      }
    };

    if (open) {
      loadBalance();
    }
  }, [open, fetchAiCreditsBalance]);

  const handleClose = () => {
    setStep(STEP_CONFIG.SETUP);
    setTone("formal");
    setCommentLength("medium");
    setFile(null);
    setPreviewRows([]);
    setQuotaExceeded(false);
    setNoEmail(false);
    setAiPendingRef(null);
    setFwPendingConfig(null);
    fwPendingRef.current = null;
    onClose();
  };

  const handleGenerate = async () => {
    setStep(STEP_CONFIG.LOADING);
    try {
      const formData = new FormData();
      formData.append("role_type", roleType);
      formData.append("tone", tone);
      formData.append("comment_length", commentLength);
      formData.append("type", commentType);
      if (students.length > 0) {
        formData.append(
          "student_ids",
          JSON.stringify(students.map((student) => student.id))
        );
      }
      if (subjectId) formData.append("subject_id", subjectId);
      if (classroomId) formData.append("classroom_id", classroomId);
      if (sectionId) formData.append("section_id", sectionId);
      if (departmentId) formData.append("department_id", departmentId);
      if (termId) formData.append("term_id", termId);
      if (file) formData.append("file", file);

      const result = await generateAiComments(formData).unwrap();
      const generated = result.comments || [];

      // Map to preview rows
      const rows = generated.map((g) => {
        const student = students.find(
          (s) => String(s.id) === String(g.student_id)
        );
        const hasExisting = existingComments.some(
          (ec) => String(ec.student_id) === String(g.student_id) && ec.content
        );
        return {
          student_id: g.student_id,
          name: getStudentDisplayName(student, g.student_id),
          content: g.content || "",
          selected: true,
          hasExisting,
        };
      });
      setPreviewRows(rows);
      setStep(STEP_CONFIG.PREVIEW);
    } catch (err) {
      if (err?.data?.error === "AI_QUOTA_EXCEEDED") {
        setQuotaExceeded(true);
      } else {
        toast.error(err?.data?.message || err?.data?.error || "Failed to generate comments");
      }
      setStep(STEP_CONFIG.SETUP);
    }
  };

  const handleAiPurchase = async () => {
    if (aiPurchaseAmount < 50) {
      toast.error("Minimum purchase is 50 credits (₦500).");
      return;
    }
    setAiPurchasing(true);
    try {
      const result = await purchaseAiCredits({
        credits: aiPurchaseAmount,
        gateway: aiPurchaseGateway,
      }).unwrap();
      try {
        openInlinePayment(result, async (reference) => {
          try {
            const verifyRes = await verifyAiCredits({ reference, gateway: aiPurchaseGateway }).unwrap();
            toast.success("AI credits added! Retrying generation…");
            setAiBalance(verifyRes?.balance || 0);
            setQuotaExceeded(false);
            setNoEmail(false);
            setAiPendingRef(null);
            handleGenerate();
          } catch (err) {
            toast.error(err?.data?.message || "Payment verification failed.");
          }
        });
      } catch (inlineErr) {
        if (result.gateway === "paystack" && result.payment_url) {
          setAiPendingRef(result.reference);
          openCheckoutPopup(result.payment_url);
          toast.info("Inline checkout could not start. Complete payment in the popup, then click Verify.");
        } else {
          throw inlineErr;
        }
      }
    } catch (err) {
      if (err?.data?.error === "NO_EMAIL") {
        setNoEmail(true);
      } else {
        toast.error(err?.data?.message || "Could not initialize payment.");
      }
    } finally {
      setAiPurchasing(false);
    }
  };

  const handleAiVerifyAndRetry = async () => {
    if (!aiPendingRef) return;
    setAiPurchasing(true);
    try {
      const verifyRes = await verifyAiCredits({ reference: aiPendingRef, gateway: "paystack" }).unwrap();
      toast.success("AI credits added! Retrying generation…");
      setAiBalance(verifyRes?.balance || 0);
      setQuotaExceeded(false);
      setNoEmail(false);
      setAiPendingRef(null);
      handleGenerate();
    } catch (err) {
      toast.error(err?.data?.message || "Payment verification failed.");
    } finally {
      setAiPurchasing(false);
    }
  };

  const handleContentChange = (studentId, value) => {
    setPreviewRows((prev) =>
      prev.map((r) => (r.student_id === studentId ? { ...r, content: value } : r))
    );
  };

  const handleToggleSelected = (studentId) => {
    setPreviewRows((prev) =>
      prev.map((r) =>
        r.student_id === studentId ? { ...r, selected: !r.selected } : r
      )
    );
  };

  const handleSelectAll = () => {
    const allSelected = previewRows.every((r) => r.selected);
    setPreviewRows((prev) => prev.map((r) => ({ ...r, selected: !allSelected })));
  };

  const handleSave = async () => {
    const toSave = previewRows
      .filter((r) => r.selected && r.content.trim())
      .map((r) => ({
        student_id: r.student_id,
        title: commentTitle,
        content: r.content.trim(),
      }));

    if (toSave.length === 0) {
      toast.error("No comments selected to save");
      return;
    }

    setSaving(true);
    try {
      await confirmAiComments({
        comments: toSave,
        subject_id: subjectId,
        classroom_id: classroomId,
        section_id: sectionId,
        department_id: departmentId,
        type: commentType,
        term_id: termId,
      }).unwrap();
      toast.success(`${toSave.length} comment(s) saved`);
      onConfirmed?.();
      handleClose();
    } catch (err) {
      toast.error(err?.data?.error || "Failed to save comments");
    } finally {
      setSaving(false);
    }
  };

  const allSelected = previewRows.length > 0 && previewRows.every((r) => r.selected);
  const someSelected = previewRows.some((r) => r.selected);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6">AI Comment Generator</Typography>
        </Stack>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* ── Step 0: Setup ── */}
        {step === STEP_CONFIG.SETUP && (
          <Stack spacing={3} sx={{ pt: 1 }}>
            {/* Quota exceeded banner — teacher self-purchase */}
            {quotaExceeded && (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    School AI quota exhausted
                  </Typography>
                  <Typography variant="body2">
                    Your school's AI comment credits are used up. Purchase personal AI
                    credits below to continue — credits are tied to your teacher account.
                  </Typography>
                </Alert>
                {noEmail && (
                  <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        component="a"
                        href="/dashboard/edit-profile"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Update Profile
                      </Button>
                    }
                  >
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Email address required
                    </Typography>
                    <Typography variant="body2">
                      Please add your email address in your profile, then come back to
                      complete the purchase.
                    </Typography>
                  </Alert>
                )}
                <Stack spacing={1.5}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      Personal AI credits balance:{" "}
                      <strong>
                        {aiBalanceLoading ? "Loading..." : `${Number(aiBalance || 0).toLocaleString()} credits`}
                      </strong>
                    </Typography>
                  </Alert>
                  <TextField
                    label="Credits to buy"
                    type="number"
                    size="small"
                    fullWidth
                    value={aiPurchaseAmount}
                    onChange={(e) => setAiPurchaseAmount(Number(e.target.value))}
                    inputProps={{ min: 50, step: 50 }}
                    helperText={`= ₦${(aiPurchaseAmount * 10).toLocaleString()} · 1 credit = 1 student comment`}
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Payment Gateway</InputLabel>
                    <Select
                      value={aiPurchaseGateway}
                      label="Payment Gateway"
                      onChange={(e) => setAiPurchaseGateway(e.target.value)}
                    >
                      <MenuItem value="paystack">Paystack</MenuItem>
                      <MenuItem value="flutterwave">Flutterwave</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    onClick={handleAiPurchase}
                    disabled={aiPurchasing}
                    startIcon={aiPurchasing ? <CircularProgress size={16} /> : null}
                  >
                    {aiPurchasing ? "Initialising…" : "Purchase AI Credits"}
                  </Button>
                  {aiPendingRef && (
                    <Button
                      variant="outlined"
                      color="success"
                      onClick={handleAiVerifyAndRetry}
                      disabled={aiPurchasing}
                    >
                      I Have Paid - Verify & Continue
                    </Button>
                  )}
                </Stack>
                <Divider sx={{ mt: 3 }} />
              </Box>
            )}

            {!quotaExceeded && (
              <Typography variant="body2" color="text.secondary">
                AI will generate {commentTitle.toLowerCase()} comments for{" "}
                <strong>{students.length} student(s)</strong> based on their academic
                performance, attendance, and behaviour. Review before saving.
              </Typography>
            )}

            {/* Personal AI balance — always shown so teacher knows their credit standing */}
            {!quotaExceeded && aiBalance > 0 && (
              <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  Your personal AI credits:{" "}
                  <strong>
                    {aiBalanceLoading ? "…" : `${Number(aiBalance).toLocaleString()} credits`}
                  </strong>
                  {" "}— will be used if school quota is exhausted.
                </Typography>
              </Alert>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>Tone</InputLabel>
              <Select value={tone} label="Tone" onChange={(e) => setTone(e.target.value)}>
                {TONES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Comment Length</InputLabel>
              <Select
                value={commentLength}
                label="Comment Length"
                onChange={(e) => setCommentLength(e.target.value)}
              >
                {COMMENT_LENGTHS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label} ({option.helper})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" gutterBottom>
                Optional context (audio, photo of notes, or document)
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,image/*,.pdf,.doc,.docx"
                style={{ display: "none" }}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                size="small"
              >
                {file ? file.name : "Upload file"}
              </Button>
              {file && (
                <IconButton
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Stack>
        )}

        {/* ── Step 1: Loading ── */}
        {step === STEP_CONFIG.LOADING && (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
              Generating comments…
            </Typography>
          </Box>
        )}

        {/* ── Step 2: Preview ── */}
        {step === STEP_CONFIG.PREVIEW && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review and edit comments below. Uncheck any you do not want to save.
            </Typography>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 160, fontWeight: 600 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Generated Comment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map((row) => (
                    <TableRow
                      key={row.student_id}
                      sx={{ background: row.hasExisting ? "rgba(255,152,0,0.06)" : undefined }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={row.selected}
                          onChange={() => handleToggleSelected(row.student_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.name}</Typography>
                        {row.hasExisting && (
                          <Tooltip title="Saving will overwrite the existing comment">
                            <Chip
                              icon={<WarningAmberIcon />}
                              label="Has existing"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ mt: 0.5, fontSize: 10 }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <StyledTextArea
                          value={row.content}
                          onChange={(e) =>
                            handleContentChange(row.student_id, e.target.value)
                          }
                          minRows={2}
                          style={{ width: "100%" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        {step === STEP_CONFIG.SETUP && (
          <Button
            variant="contained"
            onClick={handleGenerate}
            startIcon={<AutoAwesomeIcon />}
            disabled={students.length === 0}
          >
            Generate
          </Button>
        )}
        {step === STEP_CONFIG.PREVIEW && (
          <>
            <Button
              onClick={() => setStep(STEP_CONFIG.SETUP)}
              disabled={saving}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!someSelected || saving}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              Save Selected ({previewRows.filter((r) => r.selected).length})
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
